import React, { useEffect, useState } from "react";
import { SigmaContainer, useLoadGraph, useRegisterEvents, ControlsContainer, ZoomControl, FullScreenControl } from "@react-sigma/core";
import { UndirectedGraph } from "graphology";
import "@react-sigma/core/lib/style.css";
import { ChartFallbackPanel } from "./ChartFallbackPanel";
import { useArtifactSourceRegistry } from "../../pages/workspace/panels/artifactProvenance";
import { SourceColChip } from "./SourceColChip";

/**
 * GraphLoader — inner component that lives inside SigmaContainer's context.
 * Each render rebuilds the graphology graph from props and hands it to
 * Sigma via useLoadGraph. Sigma will (a) honor explicit x/y coordinates
 * when present and (b) skip the bundled layout step otherwise — random
 * positions are assigned here as the safe default.
 */
const GraphLoader = ({ nodes, edges, onHover }) => {
  const loadGraph = useLoadGraph();
  const registerEvents = useRegisterEvents();
  useEffect(() => {
    const g = new UndirectedGraph();
    // Track which IDs we successfully added so we can skip dangling-edge
    // sources/targets rather than throw and blow up the render.
    const validIds = new Set();
    nodes.forEach((n) => {
      if (!n || typeof n.id !== "string" || g.hasNode(n.id)) return;
      g.addNode(n.id, {
        label: typeof n.label === "string" ? n.label : n.id,
        size: typeof n.size === "number" ? n.size : 8,
        color: typeof n.color === "string" ? n.color : "#3498db",
        x: typeof n.x === "number" ? n.x : Math.random() * 100 - 50,
        y: typeof n.y === "number" ? n.y : Math.random() * 100 - 50,
      });
      validIds.add(n.id);
    });
    edges.forEach((e) => {
      if (!e || !validIds.has(e.source) || !validIds.has(e.target)) return;
      if (e.source === e.target) return;
      if (g.hasEdge(e.source, e.target) || g.hasEdge(e.target, e.source)) return;
      g.addEdge(e.source, e.target, {
        size: typeof e.size === "number" ? e.size : 1,
        color: typeof e.color === "string" ? e.color : "#cbd5e1",
        label: typeof e.label === "string" ? e.label : "",
      });
    });
    loadGraph(g);
  }, [nodes, edges, loadGraph]);
  useEffect(() => {
    registerEvents({
      enterNode: (e) => onHover?.(e.node),
      leaveNode: () => onHover?.(null),
    });
  }, [registerEvents, onHover]);
  return null;
};

/**
 * NetworkHost — canvas-side renderer for `render_network` chart payloads.
 *
 * Payload shape (the canonical wire form from the backend
 * `render_network` tool — see viz_schemas.validate_network_spec):
 *   { graph: { nodes: [{id,label,size,color,x,y}], edges: [{source,target}] }, layout: "force" }
 *
 * For backwards compatibility (legacy tests / direct callers) this also
 * accepts the flat shape `{ nodes, edges }` without the `graph` wrapper.
 */
export const NetworkHost = ({ spec, title, onRegenerate, sourceReferencesStore }) => {
  // Call hooks unconditionally before any early return (React rules-of-hooks).
  const [hoverNode, setHoverNode] = useState(null);
  const { bySpecId, rowSourceId, columnSourceId, storeIdFor, resolveStoreId } = useArtifactSourceRegistry(
    spec,
    sourceReferencesStore,
    { turnId: `network-${spec?.chart_id || spec?.id || "anon"}` },
  );

  const graph = spec && typeof spec === "object" && spec.graph && typeof spec.graph === "object"
    ? spec.graph
    : spec;
  if (!graph || !Array.isArray(graph.nodes) || !Array.isArray(graph.edges)) {
    return <ChartFallbackPanel title={title} error="Invalid graph payload" onRegenerate={onRegenerate} />;
  }

  // Resolve at click time so a chip whose storeId hasn't registered yet
  // (mid-stream) or a plexId-only source still lands on the Sources row.
  const handleSourceClick = (storeId, src) => {
    const id = storeId || (src?.id && resolveStoreId ? resolveStoreId(src.id, { synthesize: true }) : null);
    if (id && sourceReferencesStore?.scrollToSource) sourceReferencesStore.scrollToSource(id);
  };

  const edgeTypeSources = graph.edgeTypeSources || {};
  const legend = Object.keys(edgeTypeSources)
    .map((type) => {
      const sid = columnSourceId(null, type);
      const src = sid ? bySpecId.get(sid) : null;
      return src ? { type, src, storeId: storeIdFor(sid) } : null;
    })
    .filter(Boolean);

  const nodeById = new Map((graph.nodes || []).map((n, idx) => [n.id, { n, idx }]));
  const hovered = hoverNode ? nodeById.get(hoverNode) : null;
  const hoverSid = hovered ? rowSourceId(hovered.n, hovered.idx) : null;
  const hoverSrc = hoverSid ? bySpecId.get(hoverSid) : null;

  return (
    <div
      className="network-host"
      style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}
      data-testid="network-host"
    >
      {title && (
        <div
          className="network-host__title"
          style={{ padding: "6px 12px", fontWeight: 600, fontSize: "13px", color: "#1f2937", borderBottom: "1px solid #e5e7eb" }}
        >
          {title}
        </div>
      )}
      {legend.length > 0 && (
        <div
          className="network-host__legend"
          data-testid="network-edgetype-legend"
          style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "4px 12px", borderBottom: "1px solid #e5e7eb" }}
        >
          {legend.map((l) => (
            <span key={l.type} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span className="network-host__legend-type">{l.type}</span>
              <SourceColChip src={l.src} storeId={l.storeId} onClick={handleSourceClick} />
            </span>
          ))}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        <SigmaContainer
          style={{ width: "100%", height: "100%", background: "#fff" }}
          graph={UndirectedGraph}
          settings={{
            // The canvas <aside> mounts the instant it becomes visible, so the
            // container can have 0 height for the first frame before layout.
            // Tolerate it (Sigma resizes via ResizeObserver once laid out)
            // instead of throwing "Container has no height". Matches EdgeGraph.
            allowInvalidContainer: true,
            renderLabels: true,
            labelDensity: 0.7,
            labelGridCellSize: 60,
            labelRenderedSizeThreshold: 6,
            defaultEdgeColor: "#cbd5e1",
            defaultNodeColor: "#3498db",
            minCameraRatio: 0.1,
            maxCameraRatio: 10,
          }}
        >
          <GraphLoader nodes={graph.nodes} edges={graph.edges} onHover={setHoverNode} />
          <ControlsContainer position="bottom-right">
            <ZoomControl />
            <FullScreenControl />
          </ControlsContainer>
        </SigmaContainer>
        {hoverSrc && (
          <div
            className="network-host__node-tooltip"
            data-testid="network-node-tooltip"
            style={{ position: "absolute", top: 8, left: 8, zIndex: 5, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 4, padding: "2px 6px" }}
          >
            <SourceColChip src={hoverSrc} storeId={storeIdFor(hoverSid)} onClick={handleSourceClick} />
          </div>
        )}
      </div>
    </div>
  );
};
